import { FC } from 'react'
import { TrendBadge } from 'src/components/TrendBadge'
import { formatSigned } from '../utils'

type DeltaKind = 'percent' | 'points' | 'position'

interface DeltaBadgeProps {
  className?: string
  // How the change is read: percentage points (rates), % change (counts),
  // or positions (Avg. AI Position, where lower is better).
  kind: DeltaKind
  lowerIsBetter?: boolean
  value: number | null | undefined
}

const digitsFor = (kind: DeltaKind, value: number): number => {
  if (kind === 'position') return 1
  if (kind === 'points') return 0
  return Math.abs(value) < 10 ? 1 : 0
}

const isMissing = (
  value: number | null | undefined
): value is null | undefined =>
  value === null || value === undefined || !Number.isFinite(value)

/**
 * The tone of a change as it is shown: a +0.04 position reads "0.0", so it is
 * flat (grey), not a rise. Null when there is nothing to compare with.
 */
export const deltaTone = (
  value: number | null | undefined,
  kind: DeltaKind,
  lowerIsBetter = kind === 'position'
): 'bad' | 'flat' | 'good' | null => {
  if (isMissing(value)) return null
  const shown = Number(value.toFixed(digitsFor(kind, value)))
  if (shown === 0) return 'flat'
  return shown > 0 !== lowerIsBetter ? 'good' : 'bad'
}

// "vs previous period" pill. Nothing to compare with renders nothing.
export const DeltaBadge: FC<DeltaBadgeProps> = ({
  className,
  kind,
  lowerIsBetter,
  value,
}) => {
  if (isMissing(value)) return null

  const digits = digitsFor(kind, value)
  const label =
    kind === 'position'
      ? formatSigned(value, digits)
      : kind === 'points'
        ? `${formatSigned(value, digits)} pts`
        : `${formatSigned(value, digits)}%`

  return (
    <TrendBadge
      className={className}
      label={label}
      lowerIsBetter={lowerIsBetter ?? kind === 'position'}
      // The rounded value, so a "0.0" or "0 pts" label is drawn flat.
      value={Number(value.toFixed(digits))}
    />
  )
}
